@if (!isLoading) {
    <div class="flex flex-column overflow-y-auto h-full">
        @if ((onboardingInfo?.requestedSolutions?.length ?? 0 > 0) && config.client.support?.isBanker) {
            <section>
                <h2 class="section-title">{{ 'solution.common.requestedLoan' | translate }}</h2>
                <div class="card flex flex-column gap-2">
                    @for (solution of onboardingInfo?.requestedSolutions; track solution) {
                        <p-message severity="warn">
                            {{ 'solution.' + solution.solutionTemplateCode + '.title' | translate }}
                        </p-message>
                    }
                </div>
            </section>
        }
        <form [formGroup]="settingsForm" (ngSubmit)="onSubmit()">
            <div class="flex align-items-center justify-content-between gap-3 mb-3">
                <h2 class="section-title m-0">
                    {{
                        (!onboardingInfo?.name
                            ? 'components.settingsForm.companySection.title'
                            : 'components.settingsForm.companySection.title2'
                        ) | translate: { name: onboardingInfo?.name }
                    }}
                </h2>
                @if (onboardingInfo !== undefined && isMainAdmin) {
                    <span>{{ onboardingInfo.uid }}</span>
                }
                <div class="flex align-items-center gap-2">
                    <p-button
                        icon="ms ms-content_copy"
                        severity="secondary"
                        [text]="true"
                        [rounded]="true"
                        [ariaLabel]="'admin.copyUID' | translate"
                        [pTooltip]="'admin.copyUID' | translate"
                        (onClick)="copyUid()" />
                    <span>{{ onboardingInfo?.uid }}</span>
                </div>
            </div>
            <div class="card">
                <div class="form-grid form-grid--2" formGroupName="organizationGroup">
                    <div class="form-field form-field--wide">
                        <label for="companyLogoInput" class="form-field__label">
                            {{ 'components.settingsForm.companySection.companyLogo.label' | translate }}
                        </label>
                        <app-image-selector
                            inputId="companyLogoInput"
                            accept="image/png, image/jpeg"
                            [maxFileSize]="5 * 1024 * 1024"
                            [label]="'components.settingsForm.button.imageUpload' | translate"
                            formControlName="companyLogo"
                            styleClass="flex align-items-center"
                            [placeholder]="onboardingInfo?.name?.substring(0, 2)"></app-image-selector>
                        <small class="form-field__help">
                            {{ 'components.settingsForm.companySection.companyLogo.accept' | translate }}
                        </small>
                        @if (
                            organizationFormGroup.controls.companyLogo.invalid &&
                            organizationFormGroup.controls.companyLogo.touched
                        ) {
                            @if (organizationFormGroup.controls.companyLogo.errors?.invalidFileType) {
                                <small class="p-error">{{
                                    'components.settingsForm.companySection.toasts.avatarUpload.invalideTypeFileError'
                                        | translate
                                }}</small>
                            }
                            @if (organizationFormGroup.controls.companyLogo.errors?.fileSizeExceeded) {
                                <small class="p-error">{{
                                    'components.settingsForm.companySection.toasts.avatarUpload.maxFileSizeError'
                                        | translate
                                }}</small>
                            }
                        }
                    </div>
                    <div class="form-field">
                        <label for="companyNameInput" class="form-field__label form-field__label--required">
                            {{ 'components.settingsForm.companySection.companyName.label' | translate }}
                        </label>
                        <input
                            id="companyNameInput"
                            pInputText
                            type="text"
                            formControlName="companyName"
                            [placeholder]="
                                'components.settingsForm.companySection.companyName.placeholder' | translate
                            " />
                        @if (
                            organizationFormGroup.controls.companyName.invalid &&
                            organizationFormGroup.controls.companyName.errors?.required &&
                            organizationFormGroup.controls.companyName.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                    <div class="form-field">
                        <label for="vatNumberInput" class="form-field__label">
                            {{ 'components.settingsForm.companySection.vatNumber.label' | translate }}
                            <span
                                class="material-symbols-outlined icon-sm cursor-help vertical-align-middle"
                                aria-hidden="true"
                                [pTooltip]="'components.settingsForm.companySection.vatNumber.infoType' | translate"
                                >info</span
                            >
                        </label>
                        <input
                            id="vatNumberInput"
                            pInputText
                            type="text"
                            formControlName="vatNumber"
                            [placeholder]="vatPlaceholder"
                            (blur)="onVatBlur()" />
                        @if (
                            organizationFormGroup.controls.vatNumber.invalid &&
                            organizationFormGroup.controls.vatNumber.errors?.required &&
                            organizationFormGroup.controls.vatNumber.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                        @if (
                            organizationFormGroup.controls.vatNumber.invalid &&
                            organizationFormGroup.controls.vatNumber.errors?.invalidVat &&
                            organizationFormGroup.controls.vatNumber.touched
                        ) {
                            <small class="p-error">{{
                                'components.settingsForm.companySection.vatNumber.invalidVat'
                                    | translate: { vatFormat: vatPlaceholder }
                            }}</small>
                        }
                    </div>
                    <div class="form-field">
                        <label for="countryInput" class="form-field__label form-field__label--required">
                            {{ 'components.settingsForm.entitySection.edit.country.label' | translate }}
                        </label>
                        <p-select
                            inputId="countryInput"
                            [options]="countries"
                            formControlName="country"
                            [placeholder]="'components.settingsForm.entitySection.edit.country.placeholder' | translate"
                            [filter]="true"
                            filterMatchMode="startsWith"
                            optionLabel="name"
                            optionValue="code"
                            appendTo="body"
                            [emptyFilterMessage]="'common.filter.empty' | translate"
                            [virtualScroll]="true"
                            [virtualScrollItemSize]="38"
                            (onBlur)="filterCities()"
                            (onChange)="onCountryChange($event)"
                            [filterPlaceholder]="'common.search' | translate">
                        </p-select>
                        @if (
                            organizationFormGroup.controls.country.invalid &&
                            organizationFormGroup.controls.country.errors?.required &&
                            organizationFormGroup.controls.country.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                    <div class="form-field">
                        <label for="cityInput" class="form-field__label">
                            {{ 'components.settingsForm.entitySection.edit.city.label' | translate }}
                        </label>
                        <p-select
                            inputId="cityInput"
                            [options]="filteredCities"
                            formControlName="city"
                            [placeholder]="'components.settingsForm.entitySection.edit.city.placeholder' | translate"
                            [filter]="true"
                            filterMatchMode="startsWith"
                            optionLabel="name"
                            optionValue="id"
                            appendTo="body"
                            [emptyFilterMessage]="'common.filter.empty' | translate"
                            [virtualScroll]="true"
                            [virtualScrollItemSize]="38"
                            [filterPlaceholder]="'common.search' | translate">
                        </p-select>
                        @if (
                            organizationFormGroup.controls.city.invalid &&
                            organizationFormGroup.controls.city.errors?.required &&
                            organizationFormGroup.controls.city.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                    <div class="form-field form-field--wide">
                        <label for="naceCodeInput" class="form-field__label">
                            {{ 'components.settingsForm.companySection.naceCode.label' | translate }}
                        </label>
                        <p-select
                            inputId="naceCodeInput"
                            [options]="activities"
                            formControlName="naceCode"
                            [placeholder]="'components.settingsForm.companySection.naceCode.placeholder' | translate"
                            [showClear]="true"
                            [filter]="true"
                            optionValue="id"
                            appendTo="body"
                            [emptyFilterMessage]="'common.filter.empty' | translate"
                            [virtualScroll]="true"
                            [virtualScrollItemSize]="38"
                            [filterPlaceholder]="'common.search' | translate">
                            <ng-template let-sa pTemplate="selectedItem">
                                <div class="white-space-nowrap overflow-hidden text-overflow-ellipsis">
                                    {{ sa.id }} ({{ sa.label }})
                                </div>
                            </ng-template>
                            <ng-template let-activity pTemplate="item">
                                <div>{{ activity.id }} ({{ activity.label }})</div>
                            </ng-template>
                        </p-select>
                        @if (
                            organizationFormGroup.controls.naceCode.invalid &&
                            organizationFormGroup.controls.naceCode.errors?.required &&
                            organizationFormGroup.controls.naceCode.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                </div>
            </div>
            @if (showOwnerSection) {
                <h2 class="section-title">{{ 'components.settingsForm.userSection.title' | translate }}</h2>
                <div class="card">
                    <div class="form-grid form-grid--2" formGroupName="userGroup">
                        <div class="form-field">
                            <label for="lastnameInput" class="form-field__label form-field__label--required">
                                {{ 'components.settingsForm.userSection.lastname.label' | translate }}
                            </label>
                            <input
                                id="lastnameInput"
                                pInputText
                                type="text"
                                formControlName="lastname"
                                [placeholder]="
                                    'components.settingsForm.userSection.lastname.placeholder' | translate
                                " />
                            @if (
                                userFormGroup.controls.lastname.invalid &&
                                userFormGroup.controls.lastname.errors?.required &&
                                userFormGroup.controls.lastname.touched
                            ) {
                                <small class="p-error">{{
                                    'common.forms.validationMessages.required' | translate
                                }}</small>
                            }
                        </div>
                        <div class="form-field">
                            <label for="firstnameInput" class="form-field__label form-field__label--required">
                                {{ 'components.settingsForm.userSection.firstname.label' | translate }}
                            </label>
                            <input
                                id="firstnameInput"
                                pInputText
                                type="text"
                                formControlName="firstname"
                                [placeholder]="
                                    'components.settingsForm.userSection.firstname.placeholder' | translate
                                " />
                            @if (
                                userFormGroup.controls.firstname.invalid &&
                                userFormGroup.controls.firstname.errors?.required &&
                                userFormGroup.controls.firstname.touched
                            ) {
                                <small class="p-error">{{
                                    'common.forms.validationMessages.required' | translate
                                }}</small>
                            }
                        </div>
                        <div class="form-field">
                            <label for="roleInput" class="form-field__label">
                                {{ 'components.settingsForm.userSection.role.label' | translate }}
                            </label>
                            <input
                                id="roleInput"
                                pInputText
                                type="text"
                                formControlName="role"
                                [placeholder]="'components.settingsForm.userSection.role.placeholder' | translate" />
                        </div>
                        <div class="form-field">
                            <label for="emailInput" class="form-field__label form-field__label--required">
                                {{ 'components.settingsForm.userSection.email.label' | translate }}
                            </label>
                            <input
                                id="emailInput"
                                pInputText
                                type="text"
                                formControlName="email"
                                [placeholder]="'components.settingsForm.userSection.email.placeholder' | translate" />
                            @if (
                                userFormGroup.controls.email.invalid &&
                                userFormGroup.controls.email.errors?.required &&
                                userFormGroup.controls.email.touched
                            ) {
                                <small class="p-error">{{
                                    'common.forms.validationMessages.required' | translate
                                }}</small>
                            }
                            @if (
                                userFormGroup.controls.email.invalid &&
                                userFormGroup.controls.email.errors?.invalidEmail &&
                                userFormGroup.controls.email.touched
                            ) {
                                <small class="p-error">{{
                                    'components.settingsForm.userSection.email.validation.invalid' | translate
                                }}</small>
                            }
                        </div>
                        <div class="form-field">
                            <label for="phoneNumberInput" class="form-field__label">
                                {{ 'components.settingsForm.userSection.phoneNumber.label' | translate }}
                            </label>
                            <input
                                id="phoneNumberInput"
                                pInputText
                                type="text"
                                formControlName="phoneNumber"
                                [placeholder]="
                                    'components.settingsForm.userSection.phoneNumber.placeholder' | translate
                                " />
                            @if (
                                userFormGroup.controls.phoneNumber.invalid &&
                                userFormGroup.controls.phoneNumber.errors?.required &&
                                userFormGroup.controls.phoneNumber.touched
                            ) {
                                <small class="p-error">{{
                                    'common.forms.validationMessages.required' | translate
                                }}</small>
                            }
                            @if (
                                userFormGroup.controls.phoneNumber.invalid &&
                                userFormGroup.controls.phoneNumber.errors?.invalidPn &&
                                userFormGroup.controls.phoneNumber.touched
                            ) {
                                <small class="p-error">{{
                                    'components.settingsForm.userSection.phoneNumber.validation.invalid' | translate
                                }}</small>
                            }
                        </div>
                        <div class="form-field">
                            <label for="defaultLocaleInput" class="form-field__label">
                                {{ 'components.settingsForm.userSection.defaultLocale.label' | translate }}
                            </label>
                            <p-select
                                inputId="defaultLocaleInput"
                                [options]="availableLanguages"
                                formControlName="defaultLocale"
                                appendTo="body">
                                <!-- Language codes read in capitals (FR, NL…). -->
                                <ng-template pTemplate="selectedItem" let-selected>{{
                                    selected | uppercase
                                }}</ng-template>
                                <ng-template let-locale pTemplate="item">{{ locale | uppercase }}</ng-template>
                            </p-select>
                        </div>
                    </div>
                </div>
            }
            <h2 class="section-title">{{ 'components.settingsForm.licenseSection.title' | translate }}</h2>
            <div class="card">
                <div class="form-grid form-grid--2" formGroupName="licenseGroup">
                    <div class="form-field form-field--wide">
                        <label for="licenseInput" class="form-field__label form-field__label--required">
                            {{ 'components.settingsForm.licenseSection.license.label' | translate }}
                        </label>
                        <p-select
                            inputId="licenseInput"
                            [options]="availablePlans"
                            formControlName="license"
                            appendTo="body"
                            optionValue="uid">
                            <ng-template pTemplate="selectedItem" let-license>
                                <span class="pill" [class.pill--primary]="license.name.toLowerCase() !== 'free'">
                                    {{ license.name | uppercase }}
                                </span>
                            </ng-template>
                            <ng-template let-license pTemplate="item">
                                <div class="flex align-items-center gap-3">
                                    <span class="pill" [class.pill--primary]="license.name.toLowerCase() !== 'free'">
                                        {{ license.name | uppercase }}
                                    </span>
                                    <div>
                                        <span>
                                            {{
                                                'components.settingsForm.licenseSection.license.options.users'
                                                    | translatePlural: license.doc.max_users
                                                    | translate: { amount: license.doc.max_users }
                                            }}
                                        </span>
                                        <span>
                                            {{
                                                'components.settingsForm.licenseSection.license.options.entities'
                                                    | translatePlural: license.doc.max_entities
                                                    | translate: { amount: license.doc.max_entities }
                                            }}
                                        </span>
                                        @if (license.doc.multi_period) {
                                            <span>
                                                {{
                                                    'components.settingsForm.licenseSection.license.options.multiPeriods'
                                                        | translate
                                                }}
                                            </span>
                                        } @else {
                                            <span>
                                                {{
                                                    'components.settingsForm.licenseSection.license.options.singlePeriod'
                                                        | translate
                                                }}
                                            </span>
                                        }
                                    </div>
                                </div>
                            </ng-template>
                        </p-select>
                        @if (
                            licenseFormGroup.controls.license.invalid &&
                            licenseFormGroup.controls.license.errors?.required &&
                            licenseFormGroup.controls.license.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                    @if (isLicenseCustomizable()) {
                        <div class="form-field">
                            <label for="licenseUserInput" class="form-field__label form-field__label--required">
                                {{ 'components.settingsForm.licenseSection.licenseUser.label' | translate }}
                            </label>
                            <p-inputNumber
                                inputId="licenseUserInput"
                                formControlName="users"
                                [showButtons]="true"
                                [min]="selectedPlan?.doc?.max_users ?? 0"></p-inputNumber>
                            @if (licenseFormGroup.controls.users.invalid && licenseFormGroup.controls.users.touched) {
                                @if (licenseFormGroup.controls.users.errors?.required) {
                                    <small class="p-error">{{
                                        'common.forms.validationMessages.required' | translate
                                    }}</small>
                                }
                                @if (licenseFormGroup.controls.users.errors?.min) {
                                    <small class="p-error">{{
                                        'common.forms.validationMessages.min'
                                            | translate: { min: licenseFormGroup.controls.users.errors?.min?.min }
                                    }}</small>
                                }
                            }
                        </div>
                        <div class="form-field">
                            <label for="licenseEntityInput" class="form-field__label form-field__label--required">
                                {{ 'components.settingsForm.licenseSection.licenseEntity.label' | translate }}
                            </label>
                            <p-inputNumber
                                inputId="licenseEntityInput"
                                formControlName="entities"
                                [showButtons]="true"
                                [min]="selectedPlan?.doc?.max_entities ?? 0"></p-inputNumber>
                            @if (
                                licenseFormGroup.controls.entities.invalid && licenseFormGroup.controls.entities.touched
                            ) {
                                @if (licenseFormGroup.controls.entities.errors?.required) {
                                    <small class="p-error">{{
                                        'common.forms.validationMessages.required' | translate
                                    }}</small>
                                }
                                @if (licenseFormGroup.controls.entities.errors?.min) {
                                    <small class="p-error">{{
                                        'common.forms.validationMessages.min'
                                            | translate: { min: licenseFormGroup.controls.entities.errors?.min?.min }
                                    }}</small>
                                }
                            }
                        </div>
                        <div class="form-field form-field--wide">
                            <span id="licenseModulesLabel" class="form-field__label">
                                {{ 'components.settingsForm.licenseSection.licenseModule.label' | translate }}
                            </span>
                            <div class="module-grid" role="group" aria-labelledby="licenseModulesLabel">
                                <button
                                    type="button"
                                    class="pack-card"
                                    [class.pack-card--selected]="licenseFormGroup.controls.expert.value"
                                    [attr.aria-pressed]="!!licenseFormGroup.controls.expert.value"
                                    [disabled]="licenseFormGroup.controls.expert.disabled"
                                    (click)="toggleModule('expert')">
                                    @if (licenseFormGroup.controls.expert.value) {
                                        <span class="pack-card__check">
                                            <span class="material-symbols-outlined icon-sm" aria-hidden="true"
                                                >check</span
                                            >
                                        </span>
                                    }
                                    {{ 'components.settingsForm.licenseSection.licenseModule.expert' | translate }}
                                </button>
                                <button
                                    type="button"
                                    class="pack-card"
                                    [class.pack-card--selected]="licenseFormGroup.controls.solutions.value"
                                    [attr.aria-pressed]="!!licenseFormGroup.controls.solutions.value"
                                    [disabled]="licenseFormGroup.controls.solutions.disabled"
                                    (click)="toggleModule('solutions')">
                                    @if (licenseFormGroup.controls.solutions.value) {
                                        <span class="pack-card__check">
                                            <span class="material-symbols-outlined icon-sm" aria-hidden="true"
                                                >check</span
                                            >
                                        </span>
                                    }
                                    {{ 'components.settingsForm.licenseSection.licenseModule.solutions' | translate }}
                                </button>
                                <button
                                    type="button"
                                    class="pack-card"
                                    [class.pack-card--selected]="licenseFormGroup.controls.csrd.value"
                                    [attr.aria-pressed]="!!licenseFormGroup.controls.csrd.value"
                                    [disabled]="licenseFormGroup.controls.csrd.disabled"
                                    (click)="toggleModule('csrd')">
                                    @if (licenseFormGroup.controls.csrd.value) {
                                        <span class="pack-card__check">
                                            <span class="material-symbols-outlined icon-sm" aria-hidden="true"
                                                >check</span
                                            >
                                        </span>
                                    }
                                    {{ 'components.settingsForm.licenseSection.licenseModule.csrd' | translate }}
                                </button>
                                <button
                                    type="button"
                                    class="pack-card"
                                    [class.pack-card--selected]="licenseFormGroup.controls.reduce.value"
                                    [attr.aria-pressed]="!!licenseFormGroup.controls.reduce.value"
                                    [disabled]="licenseFormGroup.controls.reduce.disabled"
                                    (click)="toggleModule('reduce')">
                                    @if (licenseFormGroup.controls.reduce.value) {
                                        <span class="pack-card__check">
                                            <span class="material-symbols-outlined icon-sm" aria-hidden="true"
                                                >check</span
                                            >
                                        </span>
                                    }
                                    {{ 'components.settingsForm.licenseSection.licenseModule.reduce' | translate }}
                                </button>
                                <!-- MCP access: per-organization grant, toggled by the platform admin only.
                                     Hidden on creation — the backend only accepts it on the pricing-plan update.
                                     The tooltip sits on a wrapper: a disabled button does not report hovers. -->
                                @if (onboardingInfo?.name) {
                                    <div
                                        class="flex"
                                        [pTooltip]="
                                            'components.settingsForm.licenseSection.licenseModule.mcpAdminOnly'
                                                | translate
                                        "
                                        [tooltipDisabled]="!licenseFormGroup.controls.mcpAccess.disabled"
                                        tooltipPosition="top">
                                        <button
                                            type="button"
                                            class="pack-card"
                                            [class.pack-card--selected]="licenseFormGroup.controls.mcpAccess.value"
                                            [attr.aria-pressed]="!!licenseFormGroup.controls.mcpAccess.value"
                                            [disabled]="licenseFormGroup.controls.mcpAccess.disabled"
                                            (click)="toggleModule('mcpAccess')">
                                            @if (licenseFormGroup.controls.mcpAccess.value) {
                                                <span class="pack-card__check">
                                                    <span class="material-symbols-outlined icon-sm" aria-hidden="true"
                                                        >check</span
                                                    >
                                                </span>
                                            }
                                            {{ 'components.settingsForm.licenseSection.licenseModule.mcp' | translate }}
                                        </button>
                                    </div>
                                }
                            </div>
                        </div>
                    }
                    <div class="form-field form-field--wide">
                        <label for="licenseValidityInput" class="form-field__label form-field__label--required">
                            {{ 'components.settingsForm.licenseSection.licenseValidity.label' | translate }}
                        </label>
                        <p-datepicker
                            inputId="licenseValidityInput"
                            formControlName="validity"
                            dateFormat="dd/mm/yy"
                            [showIcon]="true"
                            [minDate]="minDate"
                            appendTo="body" />
                        @if (
                            licenseFormGroup.controls.validity.invalid &&
                            licenseFormGroup.controls.validity.errors?.required &&
                            licenseFormGroup.controls.validity.touched
                        ) {
                            <small class="p-error">{{ 'common.forms.validationMessages.required' | translate }}</small>
                        }
                    </div>
                </div>
            </div>
            <div class="form-actions">
                @if (onboardingInfo) {
                    <p-button
                        class="form-actions__start"
                        severity="danger"
                        [text]="true"
                        icon="ms ms-delete"
                        [label]="'components.settingsForm.button.remove' | translate"
                        (onClick)="delete()" />
                }
                <p-button
                    type="submit"
                    [label]="
                        (!onboardingInfo?.name
                            ? 'components.settingsForm.button.create'
                            : 'components.settingsForm.button.save'
                        ) | translate
                    " />
            </div>
        </form>
    </div>
} @else {
    <!-- LOADING (SKELETON) -->
    <div class="w-full">
        <!-- SECTION -->
        <div class="w-full">
            <p-skeleton width="20rem" height="2rem"></p-skeleton>
            <!-- CARD -->
            <div class="card flex flex-column mt-3 gap-4">
                <!-- AVATAR INPUT -->
                <div class="flex flex-column gap-2 mb-3">
                    <p-skeleton width="10rem" height="1rem"></p-skeleton>
                    <p-skeleton width="20rem" height="1rem"></p-skeleton>
                    <div class="flex align-items-center gap-2">
                        <p-skeleton size="4rem"></p-skeleton>
                        <p-skeleton width="10rem" height="3rem"></p-skeleton>
                    </div>
                </div>
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-full mt-5">
            <p-skeleton width="20rem" height="2rem"></p-skeleton>
            <!-- CARD -->
            <div class="card flex flex-column mt-3 gap-4">
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
                <!-- ROW -->
                <div class="flex w-full gap-3">
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                    <!-- INPUT -->
                    <div class="flex flex-column w-full gap-2">
                        <p-skeleton width="10rem" height="1rem"></p-skeleton>
                        <p-skeleton width="100%" height="2rem"></p-skeleton>
                    </div>
                </div>
            </div>
        </div>
    </div>
}
<p-confirmDialog />
